import React from 'react';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Analytics, Auth } from 'aws-amplify';
import { DynamoDBClient, PutItemCommand, ScanCommand } from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';

function Contact() {
  const [subject, setSubject] = React.useState('');
  const [category, setCategory] = React.useState('feature');
  const [message, setMessage] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [loadingList, setLoadingList] = React.useState(false);
  const TABLE_NAME = 'smd-suggestions';
  const REGION = 'us-east-2';

  const getClient = React.useCallback(() => {
    const client = new DynamoDBClient({
      region: REGION,
      credentials: async () => {
        const c = await Auth.currentCredentials();
        return {
          accessKeyId: c.accessKeyId,
          secretAccessKey: c.secretAccessKey,
          sessionToken: c.sessionToken,
        };
      },
    });
    return client;
  }, []);

  const loadSuggestions = React.useCallback(async () => {
    setLoadingList(true);
    try {
      const client = getClient();
      const resp = await client.send(new ScanCommand({ TableName: TABLE_NAME }));
      const arr = (resp.Items || []).map((it) => unmarshall(it));
      arr.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
      setItems(arr);
    } catch (e) {
      // If scan fails (permissions, missing table, etc.), keep items empty but show error inline
      console.error('Failed to load suggestions:', e);
    } finally {
      setLoadingList(false);
    }
  }, [TABLE_NAME, getClient]);

  React.useEffect(() => {
    Auth.currentUserInfo()
      .then((u) => {
        const e = u?.attributes?.email || '';
        if (e) setEmail(e);
      })
      .catch(() => {});
    // Initial load from DynamoDB
    loadSuggestions();
  }, [loadSuggestions]);

  const reset = () => {
    setSubject('');
    setCategory('feature');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError('');
    if (!message.trim()) {
      setError('Please enter a suggestion.');
      return;
    }
    setSubmitting(true);

    // If an external endpoint is configured, try posting there first
    const endpoint = process.env.REACT_APP_SUGGESTIONS_ENDPOINT;
    try {
      // Compose item
      const item = {
        id: String(Date.now()),
        subject: subject.trim() || 'suggestion',
        category,
        message: message.trim(),
        email: (email || '').trim(),
        status: 'new',
        createdAt: new Date().toISOString(),
      };

      // Write directly to DynamoDB
      try {
        const client = getClient();
        await client.send(new PutItemCommand({ TableName: TABLE_NAME, Item: marshall(item) }));
        // Refresh list from DB
        await loadSuggestions();
      } catch (dbErr) {
        console.error('DynamoDB write failed:', dbErr);
        // Build a more verbose, actionable error for the UI
        const name = dbErr?.name || 'DynamoDBError';
        const msg = dbErr?.message || 'Unknown error';
        const reqId = dbErr?.$metadata?.requestId;
        const status = dbErr?.$metadata?.httpStatusCode;
        const hint = `Check IAM permissions (dynamodb:PutItem) for the Cognito identity, table name/region (${TABLE_NAME} @ ${REGION}), and trust policy for the authenticated role.`;
        const details = [
          `${name}: ${msg}`,
          status ? `HTTP ${status}` : null,
          reqId ? `RequestId ${reqId}` : null,
          hint,
        ].filter(Boolean).join(' | ');
        setError(`Failed to save suggestion to DynamoDB. ${details}`);
        throw dbErr;
      }

      if (endpoint) {
        const resp = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject, category, message, email, ts: Date.now(), table: TABLE_NAME }),
        });
        if (!resp.ok) throw new Error('Failed to submit');
      }
      // Always record an analytics event as a backup/audit trail
      try {
        Analytics.record({
          name: 'suggestion',
          immediate: true,
          attributes: {
            subject: subject.slice(0, 80) || 'suggestion',
            category,
            email: (email || '').slice(0, 80),
            // Truncate message to keep payload reasonable
            message: message.slice(0, 900),
            table: TABLE_NAME,
          },
        });
      } catch(_) {}

      setSuccess(true);
      reset();
    } catch (err) {
      // If a detailed error was already set, do not override it
      setError((prev) => prev && prev.length ? prev : 'Unable to submit right now. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl">
      <Paper elevation={3} sx={{ p: 2 }}>
        <h1 className="text-xl font-semibold mb-2">Share Feedback / Suggestions</h1>
        <p className="text-sm mb-3">Have an idea, bug report, or data request? Drop it here.</p>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {success && <Alert severity="success">Thanks! Your suggestion was submitted.</Alert>}
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              size="small"
              placeholder="Short summary (optional)"
            />

            <TextField
              select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              size="small"
            >
              <MenuItem value="feature">Feature</MenuItem>
              <MenuItem value="bug">Bug</MenuItem>
              <MenuItem value="data">Data</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>

            <TextField
              label="Your Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              type="email"
              placeholder="Optional, for follow-up"
            />

            <TextField
              label="Suggestion"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={4}
              placeholder="Describe what you’d like to see or what’s broken..."
            />

            <div>
              <Button type="submit" variant="contained" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit'}
              </Button>
            </div>
          </Stack>
        </form>
      </Paper>
      <div className="mt-4">
        <Paper variant="outlined" sx={{ p: 2 }}>
          <h2 className="text-lg font-semibold mb-2">Current Suggestions</h2>
          {loadingList && <Alert severity="info" sx={{ mb: 2 }}>Loading suggestions…</Alert>}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Suggestion</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!items || items.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                      No suggestions yet.
                    </TableCell>
                  </TableRow>
                )}
                {items && items.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.id}</TableCell>
                    <TableCell>{formatDate(it.createdAt)}</TableCell>
                    <TableCell>{it.category}</TableCell>
                    <TableCell>{it.subject}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{it.message}</TableCell>
                    <TableCell>{it.email}</TableCell>
                    <TableCell>{it.status || 'new'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>
    </div>
  );
}

function formatDate(val) {
  if (!val) return '';
  try {
    const d = new Date(val);
    if (!isNaN(d)) return d.toLocaleString();
  } catch (_) {}
  return String(val);
}

export default Contact;
